import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Empty, Form, Input, List, Modal, Popconfirm, Select, Space, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCC } from '../../contexts/CCContext';
import { ccRequest } from '../../services/CCPublicationService';
import useCCResource from './useCCResource';
import { readRegionSiblings } from './CCAdminSelector';
import { CCError, CCResourceState } from './CCFeedback';

async function districtOptions(snapshot, signal) {
  const roots = await readRegionSiblings(snapshot, null, signal);
  const provinces = (await Promise.all(roots.map((root) => readRegionSiblings(snapshot, root.id, signal)))).flat();
  const groups = [];
  // Bound concurrency; never fetch village records or geometry for this form.
  for (let index = 0; index < provinces.length; index += 2) {
    groups.push(...await Promise.all(provinces.slice(index, index + 2).map(async (province) => ({
      label: province.name,
      options: (await readRegionSiblings(snapshot, province.id, signal)).filter((r) => r.level === 'DISTRICT')
        .map((district) => ({ value: district.id, label: `${province.name} / ${district.name}`, region: district, province })),
    }))));
  }
  return groups;
}

function DistrictMembers({ ids }) {
  const cc = useCC(); const { t } = useTranslation('translation');
  const result = useCCResource(cc.snapshot ? JSON.stringify([cc.accessKey, cc.snapshot.snapshot_id, cc.refresh]) : null,
    (signal) => districtOptions(cc.snapshot, signal));
  const options = (result.data || []).flatMap((group) => group.options).filter((option) => ids.includes(option.value));
  return <>
    <Select className="ci-wide" showSearch optionFilterProp="label" aria-label={t('ci.districts')} placeholder={t('ci.districts')}
      value={null} loading={result.status === 'loading'} options={options.map(({ value, label }) => ({ value, label }))}
      onChange={(id) => {
        const item = options.find((option) => option.value === id);
        cc.selectScope({ kind: 'admin', subject_id: id, name: item.region.name, level: 'DISTRICT', bounds: item.region.bounds }, [item.province, item.region]);
      }} />
    <CCResourceState result={result} retry={cc.retry} />
  </>;
}

function RegionEditor({ initial, close, changed }) {
  const cc = useCC(); const { t } = useTranslation('translation');
  const [record, setRecord] = useState(initial);
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [districts, setDistricts] = useState(initial?.district_ids || []);
  // The current API cannot read existing assignments. Never replace an unknown list.
  const [assignmentsKnown, setAssignmentsKnown] = useState(!initial);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [userCursor, setUserCursor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [conflicted, setConflicted] = useState(false);
  const mutation = useRef(null);
  useEffect(() => () => mutation.current?.abort(), []);
  const options = useCCResource(cc.snapshot ? JSON.stringify([cc.accessKey, cc.snapshot.snapshot_id, cc.refresh]) : null,
    (signal) => districtOptions(cc.snapshot, signal));
  const userList = useCCResource(cc.can('assign_users') ? JSON.stringify([cc.accessKey, search, userCursor, cc.refresh]) : null,
    (signal) => ccRequest('assignable-users', { params: { search, cursor: userCursor, limit: 100 }, signal }));
  const save = async (operation) => {
    setBusy(true); setFailure(null);
    mutation.current = new AbortController();
    const signal = mutation.current.signal;
    try {
      const id = record?.id;
      const data = operation === 'name' ? { name: name.trim(), description, ...(id ? { expected_revision: record.revision } : {}) }
        : { expected_revision: record.revision, [operation === 'districts' ? 'district_ids' : 'user_ids']: operation === 'districts' ? districts : users };
      const result = await ccRequest(id ? `commercial-regions/${id}${operation === 'name' ? '' : `/${operation}`}` : 'commercial-regions', {
        method: !id ? 'post' : operation === 'name' ? 'patch' : 'put', data, signal,
      });
      if (signal.aborted) return;
      setRecord({ ...record, ...result.commercial_region, district_ids: operation === 'districts' ? districts : record?.district_ids || [] });
      message.success(t('ci.saved')); changed();
    } catch (error) {
      if (signal.aborted) return;
      console.error('CC region save failed', error.message); setFailure(error);
      if (error.response?.status === 409 || !error.response) setConflicted(true);
    } finally { if (!signal.aborted) setBusy(false); }
  };
  const reload = async () => {
    if (!record) { setConflicted(false); return; }
    setBusy(true);
    mutation.current = new AbortController();
    const signal = mutation.current.signal;
    try {
      const latest = await ccRequest(`commercial-regions/${record.id}`, { signal });
      if (signal.aborted) return;
      setRecord(latest); setName(latest.name); setDescription(latest.description || ''); setDistricts(latest.district_ids);
      setAssignmentsKnown(false); setConflicted(false); setFailure(null); changed();
    } catch (error) { if (!signal.aborted) setFailure(error); }
    finally { if (!signal.aborted) setBusy(false); }
  };
  return <Modal open title={t(record ? 'ci.editRegion' : 'ci.createRegion')} width={760} onCancel={close} footer={<Button onClick={close}>{t('ci.close')}</Button>}>
    <Alert type="info" message={t('ci.separateSaves')} />
    {failure && <CCError error={failure} />}
    {conflicted && <Alert type="warning" message={t('ci.reviewSave')} action={<Button onClick={reload}>{t('ci.reloadRecord')}</Button>} />}
    <Form layout="vertical" className="ci-section">
      <Form.Item label={t('ci.name')} required><Input aria-label={t('ci.name')} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} /></Form.Item>
      <Form.Item label={t('ci.description')}><Input.TextArea aria-label={t('ci.description')} value={description} maxLength={2000} onChange={(e) => setDescription(e.target.value)} /></Form.Item>
      <Button type="primary" loading={busy} disabled={!name.trim() || conflicted} onClick={() => save('name')}>{t(record ? 'ci.saveDetails' : 'ci.createRegion')}</Button>
      <Form.Item label={`${t('ci.districts')} (${districts.length})`} className="ci-section">
        <Select mode="multiple" showSearch optionFilterProp="label" aria-label={t('ci.districts')} value={districts} onChange={setDistricts}
          options={(options.data || []).map((group) => ({ label: group.label, options: group.options.map(({ value, label }) => ({ value, label })) }))} loading={options.status === 'loading'} disabled={!record || conflicted} />
      </Form.Item>
      <CCResourceState result={options} retry={cc.retry} />
      {!cc.snapshot && <p>{t('ci.selectPublicationFirst')}</p>}
      <p>{t('ci.overlap')}</p>
      <Button disabled={!record || busy || conflicted || options.status !== 'ready'} onClick={() => save('districts')}>{t('ci.saveDistricts')}</Button>
      {cc.can('assign_users') && <>
        <Form.Item label={t('ci.assignedUsers')} className="ci-section">
          <Input.Search aria-label={t('ci.searchUsers')} placeholder={t('ci.searchUsers')} onSearch={(value) => { setSearch(value); setUserCursor(null); }} />
          <Select mode="multiple" className="ci-wide" aria-label={t('ci.assignedUsers')} value={users} onChange={setUsers}
            disabled={!record || !assignmentsKnown || conflicted} loading={userList.status === 'loading'}
            options={(userList.data?.users || []).map((user) => ({ value: user.id, label: `${user.name} · ${user.email}` }))} />
        </Form.Item>
        <CCResourceState result={userList} retry={cc.retry} />
        <Space><Button size="small" disabled={!userCursor} onClick={() => setUserCursor(null)}>{t('ci.firstPage')}</Button>
          <Button size="small" disabled={!userList.data?.next_cursor} onClick={() => setUserCursor(userList.data.next_cursor)}>{t('ci.nextPage')}</Button></Space>
        {!assignmentsKnown && <Alert type="info" message={t('ci.assignmentsUnavailable')} />}
        <Button disabled={!record || busy || conflicted || !assignmentsKnown} onClick={() => save('users')}>{t('ci.saveUsers')}</Button>
      </>}
    </Form>
  </Modal>;
}

export default function CCTerritories() {
  const cc = useCC(); const { t } = useTranslation('translation');
  const [cursor, setCursor] = useState(null);
  const [selected, setSelected] = useState(null);
  const [editor, setEditor] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => { setCursor(null); setError(null); setBusy(false); }, [cc.accessKey, cc.mode]);
  const archiveRequest = useRef(null);
  useEffect(() => () => archiveRequest.current?.abort(), [cc.accessKey]);
  const changed = () => setRevision((value) => value + 1);
  const listKey = JSON.stringify([cc.accessKey, cc.mode, cursor, revision, cc.refresh]);
  const result = useCCResource(cc.accessKey ? listKey : null, (signal) => ccRequest(cc.mode === 'me' ? 'me/territories' : 'commercial-regions', { params: { cursor, limit: 20 }, signal }));
  const id = selected?.access === cc.accessKey ? selected.id : null;
  const detail = useCCResource(id ? JSON.stringify([cc.accessKey, id, revision, cc.refresh]) : null, (signal) => ccRequest(`commercial-regions/${id}`, { signal }));
  const selectScope = useRef(cc.selectScope); selectScope.current = cc.selectScope;
  useEffect(() => {
    if (detail.data) selectScope.current({ kind: 'commercial_region', subject_id: detail.data.id,
      membership_revision: detail.data.membership_revision, name: detail.data.name });
  }, [detail.data, cc.scopeKey]);
  const archive = async () => {
    setBusy(true); setError(null);
    archiveRequest.current = new AbortController();
    const { signal } = archiveRequest.current;
    try {
      await ccRequest(`commercial-regions/${id}`, { method: 'delete', data: { expected_revision: detail.data.revision }, signal });
      if (signal.aborted) return;
      setSelected(null); cc.selectScope(null); changed(); message.success(t('ci.archived'));
    } catch (failure) { if (!signal.aborted) { setError(failure); changed(); } }
    finally { if (!signal.aborted) setBusy(false); }
  };
  return <section className="ci-section">
    {cc.mode === 'manage' && cc.can('manage_regions') && <Button type="primary" onClick={() => setEditor({ initial: null, access: cc.accessKey })}>{t('ci.createRegion')}</Button>}
    <CCResourceState result={result} retry={cc.retry} />
    {result.data && <>
      {cc.mode === 'me' && <><p>{t('ci.distinctDistricts')}: {result.data.effective_district_ids?.length ?? '—'}</p><p className="ci-muted">{t('ci.overlap')}</p>
        <Button onClick={() => { setSelected(null); cc.selectScope({ kind: 'me' }); }}>{t('ci.myUnion')}</Button>
        {!!result.data.effective_district_ids?.length && <DistrictMembers ids={result.data.effective_district_ids} />}</>}
      {!result.data.commercial_regions.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t(cc.mode === 'me' ? 'ci.noAssignments' : 'ci.noRegions')} />}
      <List size="small" dataSource={result.data.commercial_regions} renderItem={(region) => <List.Item>
        <Button type={id === region.id ? 'primary' : 'text'} onClick={() => setSelected({ access: cc.accessKey, id: region.id })}>{region.name}</Button>
      </List.Item>} />
      <Space><Button size="small" disabled={!cursor} onClick={() => setCursor(null)}>{t('ci.firstPage')}</Button>
        <Button size="small" disabled={!result.data.next_cursor} onClick={() => setCursor(result.data.next_cursor)}>{t('ci.nextPage')}</Button></Space>
    </>}
    <CCResourceState result={detail} retry={cc.retry} />
    {error && <CCError error={error} retry={cc.retry} />}
    {detail.data && <>
      <Typography.Title level={5}>{detail.data.name}</Typography.Title>
      <p>{detail.data.description}</p><p>{t('ci.districts')}: {detail.data.district_ids.length}</p>
      <DistrictMembers ids={detail.data.district_ids} />
      <Alert type="info" message={t('ci.assignmentsUnavailable')} />
      {cc.can('manage_regions') && cc.mode === 'manage' && <Space wrap>
        <Button onClick={() => setEditor({ initial: detail.data, access: cc.accessKey })}>{t('ci.editRegion')}</Button>
        <Popconfirm title={t('ci.archiveConfirm')} onConfirm={archive} okText={t('ci.archive')} cancelText={t('ci.close')}>
          <Button danger loading={busy}>{t('ci.archive')}</Button>
        </Popconfirm>
      </Space>}
    </>}
    {editor && editor.access === cc.accessKey && <RegionEditor key={editor.initial?.id || 'new'} initial={editor.initial} close={() => setEditor(null)} changed={changed} />}
  </section>;
}
